import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SentryErrorHandler } from "@sentry/angular";
import { exhaustMap, Subject } from "rxjs";

import { INTERCEPTOR_FAIL } from "constants/notifications.constants";
import { NotificationService } from "services/notification.service";
import { AuthFacade } from "state/auth.facade";
import { SentryService } from "../services/sentry.service";

/**
 * Global handler for errors unhandled neither by the interceptor, nor by services.
 * Since Angular doesn't allow to provide multiple ErrorHandler implementations,
 * need to extend Sentry's ErrorHandler to be able to use it in the app:
 * https://docs.sentry.io/platforms/javascript/guides/angular/features/error-handler/#multiple-error-handlers
 */
@Injectable()
export class GlobalErrorHandler extends SentryErrorHandler {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sentryService = inject(SentryService);
  private readonly notificationService = inject(NotificationService);
  private readonly authFacade = inject(AuthFacade);

  private logoutCommands = new Subject<void>();

  public constructor() {
    super({
      // TODO: Shall we show the error reporting dialog to users?
      showDialog: false,
    });

    // There might be multiple logout commands, so throttle them
    this.logoutCommands
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        exhaustMap(() => this.authFacade.logout$(true)) // after login, return to current URL
      )
      .subscribe(() => {
        this.notificationService.displayError(
          INTERCEPTOR_FAIL.SESSION_EXPIRED,
          INTERCEPTOR_FAIL.SESSION_EXPIRED_TITLE
        );
      });
  }

  public override handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      if (error.status === HttpStatusCode.Unauthorized) {
        // Unhandled authentication error, e.g. due to expired cookies
        this.logoutCommands.next();
      }

      if (error.status === HttpStatusCode.NotFound) {
        // Status 404 means the requested resource could mot be found.
        this.notificationService.displayError(
          INTERCEPTOR_FAIL.NOT_FOUND,
          INTERCEPTOR_FAIL.NOT_FOUND_TITLE
        );
      }
    }

    // Let Sentry's error handler capture errors
    if (this.sentryService.isInitialized()) {
      super.handleError(error);
    } else {
      console.error("Unhandled error (Sentry disabled):", error);
    }
  }
}
